// src/components/ProtectedRoute.tsx
import Loading from 'components/Loading';
import { redirectToGoodLoka } from 'config/sso';
import { useAuth } from 'contexts/AuthContext';
import { useToast } from 'contexts/ToastContext';
import { useUser } from 'contexts/UserContext';
import UpdateBanner from 'components/UpdateBanner/UpdateBanner';
import { useVersionCheck } from 'hooks/useVersionCheck';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAppDispatch } from 'redux.toolkit/hooks';
import { setToken } from 'redux.toolkit/state/user/reducers';
import { SocketProvider } from 'socket';
import {
  getAuthToken,
  localStorageService,
  setAuthToken,
} from 'utils/localStorage';
import { getTokenFromBrowserUrl, persistSsoTokenFromUrl } from 'utils/ssoToken';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

const ProtectedRoute: React.FC = () => {
  const dispatch = useAppDispatch();
  const { login } = useAuth();
  const { fetchUser } = useUser();
  const location = useLocation();
  const { showToast } = useToast();
  const [authStatus, setAuthStatus] = useState<AuthStatus>('loading');
  const hasEverAuthenticatedRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);
  const skipNextUrlAuthEffectRef = useRef(true);

  const { updateAvailable, applyUpdate } = useVersionCheck();
  const isInGame = location.pathname.startsWith('/faritany/');

  const redirectUnauthenticated = useCallback(() => {
    localStorageService.removeItem('token');
    redirectToGoodLoka();
  }, []);

  const extractToken = useCallback((): string | null => {
    const fromUrl = persistSsoTokenFromUrl();
    if (fromUrl) {
      return fromUrl;
    }

    const hashToken = getTokenFromBrowserUrl();
    if (hashToken) {
      setAuthToken(hashToken);
      return hashToken;
    }

    const routeParams = new URLSearchParams(location.search);
    const routeToken = routeParams.get('token');
    if (routeToken) {
      setAuthToken(routeToken);
      return routeToken;
    }

    return getAuthToken();
  }, [location.search]);

  const checkAuth = useCallback(
    async (options?: { silent?: boolean }) => {
      const token = extractToken();
      if (!token) {
        setAuthStatus('unauthenticated');
        return;
      }

      lastTokenRef.current = token;

      const showBootstrapLoading =
        !options?.silent && !hasEverAuthenticatedRef.current;
      if (showBootstrapLoading) {
        setAuthStatus('loading');
      }

      try {
        const user = await fetchUser();
        if (!user) {
          const profileError = new Error('Profil introuvable') as Error & {
            status: number;
          };
          profileError.status = 401;
          throw profileError;
        }
        login(token);
        dispatch(setToken(token));
        hasEverAuthenticatedRef.current = true;
        setAuthStatus('authenticated');
      } catch (error: any) {
        console.error('SSO auth failed:', error);
        if (error?.status) {
          redirectUnauthenticated();
        }
        showToast(error?.message || 'Une erreur est survenue', 'error', 4000);
        setAuthStatus('unauthenticated');
      }
    },
    [dispatch, extractToken, fetchUser, login, redirectUnauthenticated, showToast],
  );

  useEffect(() => {
    void checkAuth();
    // Bootstrap auth once on mount — tab changes must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skipNextUrlAuthEffectRef.current) {
      skipNextUrlAuthEffectRef.current = false;
      return;
    }

    const urlToken = persistSsoTokenFromUrl() ?? getTokenFromBrowserUrl();
    if (!urlToken) {
      return;
    }
    if (urlToken === lastTokenRef.current && hasEverAuthenticatedRef.current) {
      return;
    }

    void checkAuth({ silent: hasEverAuthenticatedRef.current });
  }, [location.search, location.hash, checkAuth]);

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      redirectUnauthenticated();
    }
  }, [authStatus, redirectUnauthenticated]);

  if (authStatus === 'unauthenticated') {
    return null;
  }

  const showBootstrapOverlay =
    authStatus === 'loading' && !hasEverAuthenticatedRef.current;

  return (
    <SocketProvider>
      {updateAvailable && !isInGame && (
        <UpdateBanner onUpdate={applyUpdate} />
      )}
      {showBootstrapOverlay && (
        <div
          className='loading-route-overlay loading-route-overlay--bootstrap'
          aria-busy='true'
          aria-live='polite'
        >
          <Loading />
        </div>
      )}
      {authStatus === 'authenticated' && <Outlet />}
    </SocketProvider>
  );
};

export default ProtectedRoute;
